import { deleteData, getData, postData, putData } from "../api/api";
import type {
  PrepChainPreviewResponse,
  PrepExpansionEstimateResponse,
  PrepEdgeFieldOptionsResponse,
  PrepGraphValidationResponse,
  PrepJobGraph,
  PrepJobResponse,
  PrepJobRunResponse,
  PrepJobSummaryResponse,
  PrepNodeTestResponse,
  PrepPublishResponse,
} from "../types/prep";

export type PrepJobSaveRequest = {
  name?: string;
  graph?: PrepJobGraph;
  enabled?: boolean;
};

export const prepService = {
  list: () => getData("/prep-jobs", { skipGlobalLoading: true }) as Promise<PrepJobResponse[]>,

  listSummaries: () =>
    getData("/prep-jobs/summaries", { skipGlobalLoading: true }) as Promise<PrepJobSummaryResponse[]>,

  get: (id: string) => getData(`/prep-jobs/${id}`) as Promise<PrepJobResponse>,

  create: (body: PrepJobSaveRequest) => postData("/prep-jobs", body) as Promise<PrepJobResponse>,

  update: (id: string, body: PrepJobSaveRequest) =>
    putData(`/prep-jobs/${id}`, body) as Promise<PrepJobResponse>,

  remove: (id: string) => deleteData(`/prep-jobs/${id}`),

  validate: (id: string) =>
    postData(`/prep-jobs/${id}/validate`, {}) as Promise<PrepGraphValidationResponse>,

  testNode: (id: string, nodeId: string) =>
    postData(`/prep-jobs/${id}/nodes/${nodeId}/test`, {}) as Promise<PrepNodeTestResponse>,

  resetAdaptorCursor: (id: string, nodeId: string) =>
    postData(`/prep-jobs/${id}/nodes/${nodeId}/reset-cursor`, {}),

  listAdaptors: () =>
    getData("/prep-jobs/adaptors", { skipGlobalLoading: true }) as Promise<
      import("./adminDataAdaptorService").DataAdaptorSummary[]
    >,

  previewChain: (id: string) =>
    postData(`/prep-jobs/${id}/preview-chain`, {}) as Promise<PrepChainPreviewResponse>,

  getEdgeFieldOptions: (id: string, edgeId: string) =>
    getData(`/prep-jobs/${id}/edges/${edgeId}/field-options`) as Promise<PrepEdgeFieldOptionsResponse>,

  publish: (id: string) => postData(`/prep-jobs/${id}/publish`, {}) as Promise<PrepPublishResponse>,

  estimateExpansion: (id: string) =>
    postData(`/prep-jobs/${id}/estimate-expansion`, {}) as Promise<PrepExpansionEstimateResponse>,

  run: (id: string) => postData(`/prep-jobs/${id}/run`, {}) as Promise<PrepJobRunResponse>,

  listRuns: (id: string) =>
    getData(`/prep-jobs/${id}/runs`, { skipGlobalLoading: true }) as Promise<PrepJobRunResponse[]>,

  getRun: (runId: string) => getData(`/prep-jobs/runs/${runId}`) as Promise<PrepJobRunResponse>,

  requestIndexApproval: (id: string) =>
    postData(`/prep-jobs/${id}/index-approval/request`, {}) as Promise<PrepJobResponse>,

  approveIndex: (id: string, approved: boolean) =>
    postData(`/prep-jobs/${id}/index-approval?approved=${approved}`, {}) as Promise<PrepJobResponse>,

  listPendingApprovals: () =>
    getData("/admin/prep-jobs/pending-approvals", { skipGlobalLoading: true }) as Promise<
      PrepJobSummaryResponse[]
    >,

  adminApproveIndex: (id: string, approved: boolean) =>
    postData(`/admin/prep-jobs/${id}/index-approval?approved=${approved}`, {}) as Promise<PrepJobResponse>,

  generateAnalysis: (id: string) =>
    postData(`/prep-jobs/${id}/analysis/generate`, {}) as Promise<{ analysisText: string }>,

  updateAnalysis: (id: string, analysisText: string) =>
    putData(`/prep-jobs/${id}/analysis`, { analysisText }) as Promise<PrepJobResponse>,
};
